// src/services/templateApi.ts
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
import { createAuthHeaders } from '../utils/apiHelpers';

export interface TemplateApiError {
  error: boolean;
  message: string;
  code?: 'AUTH_REQUIRED' | 'NOT_FOUND' | 'VALIDATION_ERROR' | 'NETWORK_ERROR' | 'FORBIDDEN';
  errors?: Array<{
    field: string;
    message: string;
  }>;
}

// Template list item (from GET /api/templates)
export interface TemplateListItem {
  templateId: number;
  name: string;
  description: string | null;
  status: 'ACTIVE' | 'ARCHIVED' | 'DRAFT';
  createdBy: number;
  createdAt: string;
}

export interface TemplatesListResponse {
  templates: TemplateListItem[];
}

// Template action (from GET /api/templates/:id)
export interface TemplateAction {
  templateActionId: number;
  actionType: 'CREATE' | 'REVIEW' | 'APPROVE' | 'SUBMIT' | 'NOTIFY' | 'OTHER';
  durationHours: number;
  description: string | null;
  displayOrder: number;
}

// Template task (from GET /api/templates/:id)
export interface TemplateTask {
  templateTaskId: number;
  name: string;
  durationHours: number;
  description: string | null;
  displayOrder: number;
  actions: TemplateAction[];
}

// Template phase (from GET /api/templates/:id)
export interface TemplatePhase {
  templatePhaseId: number;
  name: string;
  description: string | null;
  displayOrder: number;
  tasks: TemplateTask[];
}

// Full template structure (from GET /api/templates/:id)
export interface TemplateResponse {
  templateId: number;
  name: string;
  description: string | null;
  status: 'ACTIVE' | 'ARCHIVED' | 'DRAFT';
  createdBy: number;
  createdAt: string;
  phases: TemplatePhase[];
}

// Create template request
export interface CreateTemplateRequest {
  projectId: number;
  templateName: string;
}

// Create template response
export interface CreateTemplateResponse {
  templateId: number;
  name: string;
  status: 'DRAFT';
}

// Update template request
export interface UpdateTemplateRequest {
  name?: string;
  description?: string | null;
  phases?: TemplatePhase[];
  status?: 'ACTIVE' | 'ARCHIVED' | 'DRAFT';
}

// Get all templates (with optional status filter)
export async function getTemplates(status?: 'ACTIVE' | 'ARCHIVED'): Promise<TemplatesListResponse> {
  const queryParams = new URLSearchParams();
  if (status) {
    queryParams.append('status', status);
  }

  const url = `${API_BASE_URL}/api/templates${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as TemplateApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Access forbidden', code: 'FORBIDDEN' } as TemplateApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to fetch templates: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as TemplateApiError;
  }

  return response.json();
}

// Get full template structure
export async function getTemplate(templateId: number): Promise<TemplateResponse> {
  const response = await fetch(`${API_BASE_URL}/api/templates/${templateId}`, {
    method: 'GET',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as TemplateApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Access forbidden', code: 'FORBIDDEN' } as TemplateApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Template not found', code: 'NOT_FOUND' } as TemplateApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to fetch template: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as TemplateApiError;
  }

  return response.json();
}

// Create template from project
export async function createTemplate(data: CreateTemplateRequest): Promise<CreateTemplateResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/templates`, {
      method: 'POST',
      credentials: 'include',
      headers: createAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (response.status === 401) {
      throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as TemplateApiError;
    }

    if (response.status === 403) {
      throw { error: true, message: 'Access forbidden', code: 'FORBIDDEN' } as TemplateApiError;
    }

    if (response.status === 400) {
      let error;
      try {
        error = await response.json();
      } catch {
        error = { message: 'Validation failed' };
      }
      throw {
        error: true,
        message: error.message || 'Validation failed',
        code: 'VALIDATION_ERROR',
        errors: error.errors,
      } as TemplateApiError;
    }

    if (response.status === 404) {
      throw { 
        error: true, 
        message: 'Project not found or cannot be used to create template', 
        code: 'NOT_FOUND' 
      } as TemplateApiError;
    }

    if (!response.ok) {
      let errorMessage = `Failed to create template: ${response.statusText}`;
      try {
        const error = await response.json();
        errorMessage = error.message || errorMessage;
      } catch {
        // If response body can't be parsed, use status text
      }
      throw {
        error: true,
        message: errorMessage,
        code: 'NETWORK_ERROR',
      } as TemplateApiError;
    }

    return response.json();
  } catch (error) {
    // Handle network errors (connection reset, timeout, etc.)
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw {
        error: true,
        message: 'Network error: Unable to connect to server. Please check your connection and try again.',
        code: 'NETWORK_ERROR',
      } as TemplateApiError;
    }
    // Re-throw if it's already a TemplateApiError
    if (error && typeof error === 'object' && 'error' in error && 'code' in error) {
      throw error;
    }
    // Otherwise, wrap in a generic error
    throw {
      error: true,
      message: error instanceof Error ? error.message : 'Failed to create template',
      code: 'NETWORK_ERROR',
    } as TemplateApiError;
  }
}

// Update template structure
export async function updateTemplate(
  templateId: number,
  data: UpdateTemplateRequest
): Promise<TemplateResponse> {
  const response = await fetch(`${API_BASE_URL}/api/templates/${templateId}`, {
    method: 'PUT',
    credentials: 'include',
    headers: createAuthHeaders(),
    body: JSON.stringify(data),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as TemplateApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Access forbidden', code: 'FORBIDDEN' } as TemplateApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Template not found', code: 'NOT_FOUND' } as TemplateApiError;
  }

  if (response.status === 400) {
    const error = await response.json();
    throw {
      error: true,
      message: error.message || 'Validation failed',
      code: 'VALIDATION_ERROR',
      errors: error.errors,
    } as TemplateApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to update template: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as TemplateApiError;
  }

  return response.json();
}

// Activate template
export async function activateTemplate(templateId: number): Promise<TemplateResponse> {
  const response = await fetch(`${API_BASE_URL}/api/templates/${templateId}/activate`, {
    method: 'PATCH',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as TemplateApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Access forbidden', code: 'FORBIDDEN' } as TemplateApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Template not found', code: 'NOT_FOUND' } as TemplateApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to activate template: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as TemplateApiError;
  }

  return response.json();
}

// Deactivate template
export async function deactivateTemplate(templateId: number): Promise<TemplateResponse> {
  const response = await fetch(`${API_BASE_URL}/api/templates/${templateId}/deactivate`, {
    method: 'PATCH',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as TemplateApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Access forbidden', code: 'FORBIDDEN' } as TemplateApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Template not found', code: 'NOT_FOUND' } as TemplateApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to deactivate template: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as TemplateApiError;
  }

  return response.json();
}

// Delete template (soft delete)
export async function deleteTemplate(templateId: number): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/templates/${templateId}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: createAuthHeaders(),
  });

  if (response.status === 401) {
    throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as TemplateApiError;
  }

  if (response.status === 403) {
    throw { error: true, message: 'Access forbidden', code: 'FORBIDDEN' } as TemplateApiError;
  }

  if (response.status === 404) {
    throw { error: true, message: 'Template not found', code: 'NOT_FOUND' } as TemplateApiError;
  }

  if (!response.ok) {
    throw {
      error: true,
      message: `Failed to delete template: ${response.statusText}`,
      code: 'NETWORK_ERROR',
    } as TemplateApiError;
  }
}

// Reorder request/response types
export interface ReorderRequest {
  elementType: 'phase' | 'task' | 'action';
  elementId: number;
  newPosition: number;
  parentId?: number;
}

export interface ReorderResponse {
  success: boolean;
  message: string;
  updatedElements: Array<{
    id: number;
    displayOrder: number;
  }>;
}

export interface BulkReorderRequest {
  elementType: 'phase' | 'task' | 'action';
  reorders: Array<{
    elementId: number;
    newPosition: number;
  }>;
  parentId?: number;
}

export interface BulkReorderResponse {
  success: boolean;
  message: string;
  updatedElements: Array<{
    id: number;
    displayOrder: number;
  }>;
}

// Reorder single template element
export async function reorderTemplateElement(
  templateId: number,
  elementType: 'phase' | 'task' | 'action',
  elementId: number,
  newPosition: number,
  parentId?: number
): Promise<ReorderResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/templates/${templateId}/reorder`, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        elementType,
        elementId,
        newPosition,
        parentId,
      }),
    });

    if (response.status === 401) {
      throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as TemplateApiError;
    }

    if (response.status === 403) {
      throw { error: true, message: 'Access forbidden', code: 'FORBIDDEN' } as TemplateApiError;
    }

    if (response.status === 404) {
      throw { error: true, message: 'Template or element not found', code: 'NOT_FOUND' } as TemplateApiError;
    }

    if (response.status === 400) {
      const error = await response.json();
      throw {
        error: true,
        message: error.message || 'Validation failed',
        code: 'VALIDATION_ERROR',
        errors: error.errors,
      } as TemplateApiError;
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to reorder element' }));
      throw {
        error: true,
        message: error.message || 'Failed to reorder element',
        code: 'NETWORK_ERROR',
      } as TemplateApiError;
    }

    return await response.json();
  } catch (error) {
    // Handle network errors
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw {
        error: true,
        message: 'Network error: Unable to connect to server. Please check your connection and try again.',
        code: 'NETWORK_ERROR',
      } as TemplateApiError;
    }
    // Re-throw if it's already a TemplateApiError
    if (error && typeof error === 'object' && 'error' in error && 'code' in error) {
      throw error;
    }
    // Otherwise, wrap in a generic error
    throw {
      error: true,
      message: error instanceof Error ? error.message : 'Failed to reorder element',
      code: 'NETWORK_ERROR',
    } as TemplateApiError;
  }
}

// Bulk reorder template elements
export async function bulkReorderTemplateElements(
  templateId: number,
  elementType: 'phase' | 'task' | 'action',
  reorders: Array<{ elementId: number; newPosition: number }>,
  parentId?: number
): Promise<BulkReorderResponse> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/templates/${templateId}/reorder-bulk`, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        elementType,
        reorders,
        parentId,
      }),
    });

    if (response.status === 401) {
      throw { error: true, message: 'Authentication required', code: 'AUTH_REQUIRED' } as TemplateApiError;
    }

    if (response.status === 403) {
      throw { error: true, message: 'Access forbidden', code: 'FORBIDDEN' } as TemplateApiError;
    }

    if (response.status === 404) {
      throw { error: true, message: 'Template or element not found', code: 'NOT_FOUND' } as TemplateApiError;
    }

    if (response.status === 400) {
      const error = await response.json();
      throw {
        error: true,
        message: error.message || 'Validation failed',
        code: 'VALIDATION_ERROR',
        errors: error.errors,
      } as TemplateApiError;
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to bulk reorder elements' }));
      throw {
        error: true,
        message: error.message || 'Failed to bulk reorder elements',
        code: 'NETWORK_ERROR',
      } as TemplateApiError;
    }

    return await response.json();
  } catch (error) {
    // Handle network errors
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw {
        error: true,
        message: 'Network error: Unable to connect to server. Please check your connection and try again.',
        code: 'NETWORK_ERROR',
      } as TemplateApiError;
    }
    // Re-throw if it's already a TemplateApiError
    if (error && typeof error === 'object' && 'error' in error && 'code' in error) {
      throw error;
    }
    // Otherwise, wrap in a generic error
    throw {
      error: true,
      message: error instanceof Error ? error.message : 'Failed to bulk reorder elements',
      code: 'NETWORK_ERROR',
    } as TemplateApiError;
  }
}

